import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Plus,
  X,
  Trash2,
  CheckCircle2,
  Clock,
  ListTodo,
  AlertTriangle,
  Filter,
  FolderPlus,
  Calendar,
  Save,
  Edit,
  Target,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Priority = "low" | "medium" | "high" | "urgent";
type Status = "todo" | "in_progress" | "done";
type MilestoneStatus = "pending" | "in_progress" | "completed";

type Task = {
  id: number;
  title: string;
  description: string | null;
  status: Status;
  priority: Priority;
  assignee: string | null;
  project: string | null;
  due_date: string | null;
  created_at: string;
};

type TaskMeta = {
  teamMembers: string[];
  projects: string[];
  statuses: string[];
};

type ProjectEntry = {
  id: number;
  name: string;
  description?: string | null;
  notes?: string | null;
  color?: string | null;
  created_at?: string;
};

type Client = {
  id: number;
  name: string;
  contact_name?: string;
  email?: string;
};

type Milestone = {
  id: number;
  name: string;
  description: string | null;
  due_date: string | null;
  status: MilestoneStatus;
  client_id: number | null;
  project_id: number | null;
  created_at: string;
  client_name?: string | null;
  project_name?: string | null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PRIORITY_COLORS: Record<Priority, string> = {
  low: "border-l-gray-400",
  medium: "border-l-blue-500",
  high: "border-l-orange-500",
  urgent: "border-l-red-600",
};

const PRIORITY_BADGE: Record<Priority, string> = {
  low: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  medium: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  urgent: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

const STATUS_LABELS: Record<Status, string> = {
  todo: "To Do",
  in_progress: "In Progress",
  done: "Done",
};

const MILESTONE_STATUS_LABELS: Record<MilestoneStatus, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  completed: "Completed",
};

const MILESTONE_STATUS_BADGE: Record<MilestoneStatus, string> = {
  pending: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  in_progress: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  completed: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
};

const PROJECT_COLORS = [
  "#264027",
  "#3c5233",
  "#b38a58",
  "#6366f1",
  "#8b5cf6",
  "#ec4899",
  "#f59e0b",
  "#10b981",
];

const COLUMNS: Status[] = ["todo", "in_progress", "done"];

function initials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function formatDate(d: string | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function formatDateShort(d: string | null) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isOverdue(d: string | null) {
  if (!d) return false;
  return new Date(d) < new Date(new Date().toDateString());
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function TaskBoard() {
  const { token } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState<"tasks" | "projects" | "milestones">("tasks");

  // Tasks tab state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [meta, setMeta] = useState<TaskMeta>({ teamMembers: [], projects: [], statuses: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterProject, setFilterProject] = useState("all");

  // Inline add form per column
  const [addingIn, setAddingIn] = useState<Status | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newAssignee, setNewAssignee] = useState("");
  const [newProject, setNewProject] = useState("");
  const [newPriority, setNewPriority] = useState<Priority>("medium");
  const [saving, setSaving] = useState(false);

  // Edit sidebar
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [editForm, setEditForm] = useState<Partial<Task>>({});
  const [editSaving, setEditSaving] = useState(false);

  // Projects tab state
  const [projects, setProjects] = useState<ProjectEntry[]>([]);
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [addingNewProject, setAddingNewProject] = useState(false);
  const [newProjectForm, setNewProjectForm] = useState({
    name: "",
    description: "",
    notes: "",
    color: "#264027",
  });
  const [projectForms, setProjectForms] = useState<Record<number, Partial<ProjectEntry>>>({});

  // Milestones tab state
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [addingMilestone, setAddingMilestone] = useState(false);
  const [editMilestone, setEditMilestone] = useState<Milestone | null>(null);
  const [milestoneForm, setMilestoneForm] = useState({
    name: "",
    description: "",
    due_date: "",
    status: "pending" as MilestoneStatus,
    client_id: null as number | null,
    project_id: null as number | null,
  });

  // -----------------------------------------------------------------------
  // Data fetching
  // -----------------------------------------------------------------------

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
    [token],
  );

  const loadTasksData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tasksRes, metaRes] = await Promise.all([
        apiFetch("/api/ops/tasks", { headers }),
        apiFetch("/api/ops/tasks/meta", { headers }),
      ]);
      if (!tasksRes.ok) throw new Error(`Failed to load tasks (${tasksRes.status})`);
      const tasksData = await tasksRes.json();
      setTasks(tasksData.tasks ?? tasksData ?? []);

      if (metaRes.ok) {
        const metaData = await metaRes.json();
        setMeta(metaData);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const loadProjectsData = useCallback(async () => {
    setProjectsLoading(true);
    try {
      const res = await apiFetch("/api/ops/tasks/projects", { headers });
      if (!res.ok) throw new Error("Failed to load projects");
      const data = await res.json();
      setProjects(data.projects ?? data ?? []);
    } catch (err: any) {
      setError(err?.message || "Failed to load projects");
    } finally {
      setProjectsLoading(false);
    }
  }, [headers]);

  const loadMilestonesData = useCallback(async () => {
    setMilestonesLoading(true);
    try {
      const [milestonesRes, clientsRes] = await Promise.all([
        apiFetch("/api/ops/milestones", { headers }),
        apiFetch("/api/ops/clients", { headers }),
      ]);
      if (milestonesRes.ok) {
        const data = await milestonesRes.json();
        setMilestones(data.milestones ?? data ?? []);
      }
      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients ?? data ?? []);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load milestones");
    } finally {
      setMilestonesLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (activeTab === "tasks") {
      loadTasksData();
    } else if (activeTab === "projects") {
      loadProjectsData();
    } else if (activeTab === "milestones") {
      loadMilestonesData();
      loadProjectsData(); // Also load projects for dropdown
    }
  }, [activeTab, loadTasksData, loadProjectsData, loadMilestonesData]);

  // -----------------------------------------------------------------------
  // Tasks tab: Filtered tasks per column
  // -----------------------------------------------------------------------

  const filtered = useMemo(() => {
    return tasks.filter((t) => {
      if (filterAssignee !== "all" && t.assignee !== filterAssignee) return false;
      if (filterProject !== "all" && t.project !== filterProject) return false;
      return true;
    });
  }, [tasks, filterAssignee, filterProject]);

  const columns = useMemo(() => {
    const map: Record<Status, Task[]> = { todo: [], in_progress: [], done: [] };
    for (const t of filtered) {
      if (map[t.status]) map[t.status].push(t);
    }
    return map;
  }, [filtered]);

  const urgentCount = filtered.filter((t) => t.priority === "urgent" && t.status !== "done").length;

  // -----------------------------------------------------------------------
  // Tasks tab: Add task
  // -----------------------------------------------------------------------

  const handleAdd = async (status: Status) => {
    if (!newTitle.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch("/api/ops/tasks", {
        method: "POST",
        headers,
        body: JSON.stringify({
          title: newTitle.trim(),
          status,
          assignee: newAssignee || null,
          project: newProject || null,
          priority: newPriority,
        }),
      });
      if (!res.ok) throw new Error("Failed to create task");
      setNewTitle("");
      setNewAssignee("");
      setNewProject("");
      setNewPriority("medium");
      setAddingIn(null);
      await loadTasksData();
    } catch (err: any) {
      setError(err?.message || "Failed to create task");
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------------------------------------------------
  // Tasks tab: Move task (status change via column)
  // -----------------------------------------------------------------------

  const moveTask = async (taskId: number, newStatus: Status) => {
    try {
      const res = await apiFetch(`/api/ops/tasks/${taskId}/move`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error("Failed to move task");
      setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)));
    } catch (err: any) {
      setError(err?.message || "Failed to move task");
    }
  };

  // -----------------------------------------------------------------------
  // Tasks tab: Edit task
  // -----------------------------------------------------------------------

  const openEdit = (task: Task) => {
    setEditTask(task);
    setEditForm({ ...task });
  };

  const saveEdit = async () => {
    if (!editTask) return;
    setEditSaving(true);
    try {
      const res = await apiFetch(`/api/ops/tasks/${editTask.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error("Failed to update task");
      setEditTask(null);
      await loadTasksData();
    } catch (err: any) {
      setError(err?.message || "Failed to update task");
    } finally {
      setEditSaving(false);
    }
  };

  const deleteTask = async (id: number) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      const res = await apiFetch(`/api/ops/tasks/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Failed to delete task");
      setEditTask(null);
      await loadTasksData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete task");
    }
  };

  // -----------------------------------------------------------------------
  // Projects tab: CRUD
  // -----------------------------------------------------------------------

  const startAddProject = () => {
    setAddingNewProject(true);
    setNewProjectForm({ name: "", description: "", notes: "", color: "#264027" });
  };

  const cancelAddProject = () => {
    setAddingNewProject(false);
    setNewProjectForm({ name: "", description: "", notes: "", color: "#264027" });
  };

  const createProject = async () => {
    if (!newProjectForm.name.trim()) return;
    try {
      const res = await apiFetch("/api/ops/tasks/projects", {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: newProjectForm.name.trim(),
          description: newProjectForm.description || null,
          notes: newProjectForm.notes || null,
          color: newProjectForm.color,
        }),
      });
      if (!res.ok) throw new Error("Failed to create project");
      setAddingNewProject(false);
      setNewProjectForm({ name: "", description: "", notes: "", color: "#264027" });
      await loadProjectsData();
    } catch (err: any) {
      setError(err?.message || "Failed to create project");
    }
  };

  const startEditProject = (project: ProjectEntry) => {
    setEditingProjectId(project.id);
    setProjectForms({ ...projectForms, [project.id]: { ...project } });
  };

  const cancelEditProject = (id: number) => {
    setEditingProjectId(null);
    const { [id]: _, ...rest } = projectForms;
    setProjectForms(rest);
  };

  const updateProject = async (id: number) => {
    const form = projectForms[id];
    if (!form || !form.name?.trim()) return;
    try {
      const res = await apiFetch(`/api/ops/tasks/projects/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          name: form.name.trim(),
          description: form.description || null,
          notes: form.notes || null,
          color: form.color,
        }),
      });
      if (!res.ok) throw new Error("Failed to update project");
      setEditingProjectId(null);
      const { [id]: _, ...rest } = projectForms;
      setProjectForms(rest);
      await loadProjectsData();
    } catch (err: any) {
      setError(err?.message || "Failed to update project");
    }
  };

  const deleteProject = async (id: number) => {
    if (!window.confirm("Delete this project? Tasks won't be deleted.")) return;
    try {
      const res = await apiFetch(`/api/ops/tasks/projects/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Failed to delete project");
      await loadProjectsData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete project");
    }
  };

  // -----------------------------------------------------------------------
  // Milestones tab: CRUD
  // -----------------------------------------------------------------------

  const startAddMilestone = () => {
    setAddingMilestone(true);
    setMilestoneForm({
      name: "",
      description: "",
      due_date: "",
      status: "pending",
      client_id: null,
      project_id: null,
    });
  };

  const cancelAddMilestone = () => {
    setAddingMilestone(false);
    setMilestoneForm({
      name: "",
      description: "",
      due_date: "",
      status: "pending",
      client_id: null,
      project_id: null,
    });
  };

  const createMilestone = async () => {
    if (!milestoneForm.name.trim()) return;
    try {
      const res = await apiFetch("/api/ops/milestones", {
        method: "POST",
        headers,
        body: JSON.stringify({
          name: milestoneForm.name.trim(),
          description: milestoneForm.description || null,
          due_date: milestoneForm.due_date || null,
          status: milestoneForm.status,
          client_id: milestoneForm.client_id,
          project_id: milestoneForm.project_id,
        }),
      });
      if (!res.ok) throw new Error("Failed to create milestone");
      setAddingMilestone(false);
      setMilestoneForm({
        name: "",
        description: "",
        due_date: "",
        status: "pending",
        client_id: null,
        project_id: null,
      });
      await loadMilestonesData();
    } catch (err: any) {
      setError(err?.message || "Failed to create milestone");
    }
  };

  const openEditMilestone = (milestone: Milestone) => {
    setEditMilestone(milestone);
    setMilestoneForm({
      name: milestone.name,
      description: milestone.description || "",
      due_date: milestone.due_date ? milestone.due_date.slice(0, 10) : "",
      status: milestone.status,
      client_id: milestone.client_id,
      project_id: milestone.project_id,
    });
  };

  const closeEditMilestone = () => {
    setEditMilestone(null);
    setMilestoneForm({
      name: "",
      description: "",
      due_date: "",
      status: "pending",
      client_id: null,
      project_id: null,
    });
  };

  const updateMilestone = async () => {
    if (!editMilestone || !milestoneForm.name.trim()) return;
    try {
      const res = await apiFetch(`/api/ops/milestones/${editMilestone.id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({
          name: milestoneForm.name.trim(),
          description: milestoneForm.description || null,
          due_date: milestoneForm.due_date || null,
          status: milestoneForm.status,
          client_id: milestoneForm.client_id,
          project_id: milestoneForm.project_id,
        }),
      });
      if (!res.ok) throw new Error("Failed to update milestone");
      setEditMilestone(null);
      setMilestoneForm({
        name: "",
        description: "",
        due_date: "",
        status: "pending",
        client_id: null,
        project_id: null,
      });
      await loadMilestonesData();
    } catch (err: any) {
      setError(err?.message || "Failed to update milestone");
    }
  };

  const deleteMilestone = async (id: number) => {
    if (!window.confirm("Delete this milestone?")) return;
    try {
      const res = await apiFetch(`/api/ops/milestones/${id}`, { method: "DELETE", headers });
      if (!res.ok) throw new Error("Failed to delete milestone");
      await loadMilestonesData();
    } catch (err: any) {
      setError(err?.message || "Failed to delete milestone");
    }
  };

  // -----------------------------------------------------------------------
  // Team members list (from meta or fallback)
  // -----------------------------------------------------------------------

  const teamMembers =
    meta.teamMembers.length > 0
      ? meta.teamMembers
      : ["Johnathan", "Simon", "Sabrina", "Luis", "Gabriela"];

  const projectNames = meta.projects.length > 0 ? meta.projects : projects.map((p) => p.name);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-baseline gap-4 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Task Board</h1>
          {activeTab === "tasks" && (
            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <ListTodo size={14} className="text-gray-400" />
                <strong className="text-gray-900 dark:text-white tabular-nums">{columns.todo.length}</strong> To Do
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={14} className="text-gray-400" />
                <strong className="text-gray-900 dark:text-white tabular-nums">{columns.in_progress.length}</strong> In Progress
              </span>
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={14} className="text-gray-400" />
                <strong className="text-gray-900 dark:text-white tabular-nums">{columns.done.length}</strong> Done
              </span>
              <span className="flex items-center gap-1.5">
                <AlertTriangle size={14} className={urgentCount > 0 ? "text-red-500" : "text-gray-400"} />
                <strong className={`tabular-nums ${urgentCount > 0 ? "text-red-600 dark:text-red-400" : "text-gray-900 dark:text-white"}`}>{urgentCount}</strong> Urgent
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="flex gap-4">
          <button
            onClick={() => setActiveTab("tasks")}
            className={`text-sm font-semibold px-4 py-2 border-b-2 transition-colors ${
              activeTab === "tasks"
                ? "border-[#264027] text-[#264027] dark:text-green-400 dark:border-green-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Tasks
          </button>
          <button
            onClick={() => setActiveTab("projects")}
            className={`text-sm font-semibold px-4 py-2 border-b-2 transition-colors ${
              activeTab === "projects"
                ? "border-[#264027] text-[#264027] dark:text-green-400 dark:border-green-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Projects
          </button>
          <button
            onClick={() => setActiveTab("milestones")}
            className={`text-sm font-semibold px-4 py-2 border-b-2 transition-colors ${
              activeTab === "milestones"
                ? "border-[#264027] text-[#264027] dark:text-green-400 dark:border-green-400"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Milestones
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* TASKS TAB */}
      {activeTab === "tasks" && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-center">
            <Filter size={14} className="text-gray-400" />
            <select
              value={filterAssignee}
              onChange={(e) => setFilterAssignee(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
            >
              <option value="all">All assignees</option>
              {teamMembers.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="px-3 py-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
            >
              <option value="all">All projects</option>
              {projectNames.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <span className="text-xs text-gray-400">{filtered.length} tasks</span>
          </div>

          {/* Board */}
          {loading ? (
            <div className="text-center py-20 text-gray-400">Loading tasks...</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {COLUMNS.map((status) => (
                <div
                  key={status}
                  className="bg-gray-50 dark:bg-gray-900/50 rounded-xl p-4 min-h-[300px]"
                >
                  {/* Column header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <h2 className="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide">
                        {STATUS_LABELS[status]}
                      </h2>
                      <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full px-2 py-0.5 font-semibold">
                        {columns[status].length}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setAddingIn(addingIn === status ? null : status);
                        setNewTitle("");
                        setNewAssignee("");
                        setNewProject("");
                        setNewPriority("medium");
                      }}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-gray-500 dark:text-gray-400"
                    >
                      {addingIn === status ? <X size={14} /> : <Plus size={14} />}
                    </button>
                  </div>

                  {/* Inline add form */}
                  {addingIn === status && (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 mb-3 space-y-2">
                      <input
                        autoFocus
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        placeholder="Task title"
                        className="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-sm"
                        onKeyDown={(e) => e.key === "Enter" && handleAdd(status)}
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={newAssignee}
                          onChange={(e) => setNewAssignee(e.target.value)}
                          className="px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-xs"
                        >
                          <option value="">Assignee</option>
                          {teamMembers.map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </select>
                        <select
                          value={newProject}
                          onChange={(e) => setNewProject(e.target.value)}
                          className="px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-xs"
                        >
                          <option value="">Project</option>
                          {projectNames.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      </div>
                      <select
                        value={newPriority}
                        onChange={(e) => setNewPriority(e.target.value as Priority)}
                        className="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded text-xs"
                      >
                        <option value="low">Low Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="high">High Priority</option>
                        <option value="urgent">Urgent</option>
                      </select>
                      <button
                        onClick={() => handleAdd(status)}
                        disabled={saving || !newTitle.trim()}
                        className="w-full px-3 py-1.5 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded text-xs font-semibold transition-colors"
                      >
                        {saving ? "Saving..." : "Add Task"}
                      </button>
                    </div>
                  )}

                  {/* Task cards */}
                  <div className="space-y-2">
                    {columns[status].map((task) => (
                      <div
                        key={task.id}
                        onClick={() => openEdit(task)}
                        className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow border-l-4 ${PRIORITY_COLORS[task.priority]}`}
                      >
                        <p className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 line-clamp-2">
                            {task.description}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {task.assignee && (
                              <span
                                className="w-6 h-6 rounded-full bg-[#264027] text-white text-[10px] font-bold flex items-center justify-center"
                                title={task.assignee}
                              >
                                {initials(task.assignee)}
                              </span>
                            )}
                            {task.project && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-medium">
                                {task.project}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${PRIORITY_BADGE[task.priority]}`}
                            >
                              {task.priority}
                            </span>
                            {task.due_date && (
                              <span
                                className={`text-[10px] font-medium ${isOverdue(task.due_date) && task.status !== "done" ? "text-red-600 dark:text-red-400" : "text-gray-400"}`}
                              >
                                {formatDateShort(task.due_date)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Move buttons */}
                        <div className="flex gap-1 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                          {COLUMNS.filter((s) => s !== status).map((target) => (
                            <button
                              key={target}
                              onClick={(e) => {
                                e.stopPropagation();
                                moveTask(task.id, target);
                              }}
                              className="flex-1 text-[10px] py-1 rounded bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 font-medium transition-colors"
                            >
                              {STATUS_LABELS[target]}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Edit sidebar / modal */}
          {editTask && (
            <div className="fixed inset-0 z-50 flex justify-end">
              <div className="absolute inset-0 bg-black/30" onClick={() => setEditTask(null)} />
              <div className="relative w-full max-w-md bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl overflow-y-auto">
                <div className="p-6 space-y-5">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">Edit Task</h2>
                    <button
                      onClick={() => setEditTask(null)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  {/* Title */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Title
                    </label>
                    <input
                      value={editForm.title || ""}
                      onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Description
                    </label>
                    <textarea
                      rows={4}
                      value={editForm.description || ""}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Status
                    </label>
                    <select
                      value={editForm.status || "todo"}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value as Status })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    >
                      {COLUMNS.map((s) => (
                        <option key={s} value={s}>
                          {STATUS_LABELS[s]}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Assignee */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Assignee
                    </label>
                    <select
                      value={editForm.assignee || ""}
                      onChange={(e) =>
                        setEditForm({ ...editForm, assignee: e.target.value || null })
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    >
                      <option value="">Unassigned</option>
                      {teamMembers.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Project */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Project
                    </label>
                    <select
                      value={editForm.project || ""}
                      onChange={(e) => setEditForm({ ...editForm, project: e.target.value || null })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    >
                      <option value="">No project</option>
                      {projectNames.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Priority
                    </label>
                    <select
                      value={editForm.priority || "medium"}
                      onChange={(e) =>
                        setEditForm({ ...editForm, priority: e.target.value as Priority })
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  {/* Due date */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={editForm.due_date ? editForm.due_date.slice(0, 10) : ""}
                      onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value || null })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={saveEdit}
                      disabled={editSaving}
                      className="flex-1 px-4 py-2.5 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                    >
                      {editSaving ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      onClick={() => deleteTask(editTask.id)}
                      className="px-4 py-2.5 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded-lg text-sm font-semibold transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* PROJECTS TAB */}
      {activeTab === "projects" && (
        <div className="space-y-4">
          {/* Add New Project Button */}
          {!addingNewProject && (
            <button
              onClick={startAddProject}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#264027] hover:bg-[#1a2e1b] text-white rounded-lg text-sm font-semibold transition-colors"
            >
              <FolderPlus size={16} />
              Add New Project
            </button>
          )}

          {/* Add Project Form */}
          {addingNewProject && (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">New Project</h3>
                <button
                  onClick={cancelAddProject}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={18} />
                </button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Name *
                </label>
                <input
                  value={newProjectForm.name}
                  onChange={(e) => setNewProjectForm({ ...newProjectForm, name: e.target.value })}
                  placeholder="Project name"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={newProjectForm.description}
                  onChange={(e) =>
                    setNewProjectForm({ ...newProjectForm, description: e.target.value })
                  }
                  placeholder="Project description"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Notes
                </label>
                <textarea
                  rows={3}
                  value={newProjectForm.notes}
                  onChange={(e) => setNewProjectForm({ ...newProjectForm, notes: e.target.value })}
                  placeholder="Internal notes"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Color
                </label>
                <div className="flex gap-2">
                  {PROJECT_COLORS.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewProjectForm({ ...newProjectForm, color })}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        newProjectForm.color === color
                          ? "border-gray-900 dark:border-white scale-110"
                          : "border-gray-200 dark:border-gray-700"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={createProject}
                  disabled={!newProjectForm.name.trim()}
                  className="px-4 py-2 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  <Save size={14} className="inline mr-1.5" />
                  Create Project
                </button>
                <button
                  onClick={cancelAddProject}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Projects List */}
          {projectsLoading ? (
            <div className="text-center py-20 text-gray-400">Loading projects...</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              No projects yet. Click "Add New Project" to create one.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {projects.map((project) => {
                const isEditing = editingProjectId === project.id;
                const form = projectForms[project.id] || project;

                return (
                  <div
                    key={project.id}
                    className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 space-y-3"
                  >
                    {isEditing ? (
                      <>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                            Name *
                          </label>
                          <input
                            value={form.name || ""}
                            onChange={(e) =>
                              setProjectForms({
                                ...projectForms,
                                [project.id]: { ...form, name: e.target.value },
                              })
                            }
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                            Description
                          </label>
                          <textarea
                            rows={3}
                            value={form.description || ""}
                            onChange={(e) =>
                              setProjectForms({
                                ...projectForms,
                                [project.id]: { ...form, description: e.target.value },
                              })
                            }
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                            Notes
                          </label>
                          <textarea
                            rows={3}
                            value={form.notes || ""}
                            onChange={(e) =>
                              setProjectForms({
                                ...projectForms,
                                [project.id]: { ...form, notes: e.target.value },
                              })
                            }
                            className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                            Color
                          </label>
                          <div className="flex gap-2">
                            {PROJECT_COLORS.map((color) => (
                              <button
                                key={color}
                                onClick={() =>
                                  setProjectForms({
                                    ...projectForms,
                                    [project.id]: { ...form, color },
                                  })
                                }
                                className={`w-8 h-8 rounded-full border-2 transition-all ${
                                  form.color === color
                                    ? "border-gray-900 dark:border-white scale-110"
                                    : "border-gray-200 dark:border-gray-700"
                                }`}
                                style={{ backgroundColor: color }}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => updateProject(project.id)}
                            disabled={!form.name?.trim()}
                            className="flex-1 px-3 py-2 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                          >
                            <Save size={14} className="inline mr-1.5" />
                            Save
                          </button>
                          <button
                            onClick={() => cancelEditProject(project.id)}
                            className="px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-4 h-4 rounded-full"
                              style={{ backgroundColor: project.color || "#264027" }}
                            />
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                              {project.name}
                            </h3>
                          </div>
                          <button
                            onClick={() => startEditProject(project)}
                            className="text-gray-400 hover:text-[#264027] dark:hover:text-green-400 transition-colors"
                          >
                            <Edit size={16} />
                          </button>
                        </div>
                        {project.description && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                              Description
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              {project.description}
                            </p>
                          </div>
                        )}
                        {project.notes && (
                          <div>
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                              Notes
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{project.notes}</p>
                          </div>
                        )}
                        {project.created_at && (
                          <p className="text-xs text-gray-400">
                            Created {formatDate(project.created_at)}
                          </p>
                        )}
                        <button
                          onClick={() => deleteProject(project.id)}
                          className="w-full px-3 py-2 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded-lg text-sm font-semibold transition-colors"
                        >
                          <Trash2 size={14} className="inline mr-1.5" />
                          Delete Project
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* MILESTONES TAB */}
      {activeTab === "milestones" && (
        <div className="space-y-4">
          {/* Add Milestone Button */}
          {!addingMilestone && !editMilestone && (
            <button
              onClick={startAddMilestone}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#264027] hover:bg-[#1a2e1b] text-white rounded-lg text-sm font-semibold transition-colors"
            >
              <Target size={16} />
              Add Milestone
            </button>
          )}

          {/* Add Milestone Form */}
          {addingMilestone && (
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">New Milestone</h3>
                <button
                  onClick={cancelAddMilestone}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={18} />
                </button>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Name *
                </label>
                <input
                  value={milestoneForm.name}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                  placeholder="Milestone name"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  value={milestoneForm.description}
                  onChange={(e) =>
                    setMilestoneForm({ ...milestoneForm, description: e.target.value })
                  }
                  placeholder="Milestone description"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={milestoneForm.due_date}
                    onChange={(e) => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Status
                  </label>
                  <select
                    value={milestoneForm.status}
                    onChange={(e) =>
                      setMilestoneForm({ ...milestoneForm, status: e.target.value as MilestoneStatus })
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  >
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Client
                  </label>
                  <select
                    value={milestoneForm.client_id || ""}
                    onChange={(e) =>
                      setMilestoneForm({
                        ...milestoneForm,
                        client_id: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  >
                    <option value="">No client</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                    Project
                  </label>
                  <select
                    value={milestoneForm.project_id || ""}
                    onChange={(e) =>
                      setMilestoneForm({
                        ...milestoneForm,
                        project_id: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                  >
                    <option value="">No project</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={createMilestone}
                  disabled={!milestoneForm.name.trim()}
                  className="px-4 py-2 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                >
                  <Save size={14} className="inline mr-1.5" />
                  Create Milestone
                </button>
                <button
                  onClick={cancelAddMilestone}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Milestones List */}
          {milestonesLoading ? (
            <div className="text-center py-20 text-gray-400">Loading milestones...</div>
          ) : milestones.length === 0 ? (
            <div className="text-center py-20 text-gray-400">
              No milestones yet. Click "Add Milestone" to create one.
            </div>
          ) : (
            <div className="space-y-3">
              {milestones.map((milestone) => (
                <div
                  key={milestone.id}
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-4"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3
                          className="text-base font-bold text-gray-900 dark:text-white cursor-pointer hover:text-[#264027] dark:hover:text-green-400"
                          onClick={() => openEditMilestone(milestone)}
                        >
                          {milestone.name}
                        </h3>
                        <span
                          className={`text-xs px-2 py-1 rounded font-semibold ${MILESTONE_STATUS_BADGE[milestone.status]}`}
                        >
                          {MILESTONE_STATUS_LABELS[milestone.status]}
                        </span>
                      </div>
                      {milestone.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {milestone.description.length > 150
                            ? milestone.description.slice(0, 150) + "..."
                            : milestone.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        {milestone.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={12} />
                            {formatDate(milestone.due_date)}
                            {isOverdue(milestone.due_date) && milestone.status !== "completed" && (
                              <span className="text-red-600 dark:text-red-400 ml-1">(Overdue)</span>
                            )}
                          </span>
                        )}
                        {milestone.client_name && (
                          <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">
                            {milestone.client_name}
                          </span>
                        )}
                        {milestone.project_name && (
                          <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded">
                            {milestone.project_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteMilestone(milestone.id)}
                      className="ml-4 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Edit Milestone Sidebar */}
          {editMilestone && (
            <div className="fixed inset-0 z-50 flex justify-end">
              <div className="absolute inset-0 bg-black/30" onClick={closeEditMilestone} />
              <div className="relative w-full max-w-md bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl overflow-y-auto">
                <div className="p-6 space-y-5">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                      Edit Milestone
                    </h2>
                    <button
                      onClick={closeEditMilestone}
                      className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400"
                    >
                      <X size={18} />
                    </button>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Name *
                    </label>
                    <input
                      value={milestoneForm.name}
                      onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Description
                    </label>
                    <textarea
                      rows={4}
                      value={milestoneForm.description}
                      onChange={(e) =>
                        setMilestoneForm({ ...milestoneForm, description: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={milestoneForm.due_date}
                      onChange={(e) =>
                        setMilestoneForm({ ...milestoneForm, due_date: e.target.value })
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Status
                    </label>
                    <select
                      value={milestoneForm.status}
                      onChange={(e) =>
                        setMilestoneForm({
                          ...milestoneForm,
                          status: e.target.value as MilestoneStatus,
                        })
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Client
                    </label>
                    <select
                      value={milestoneForm.client_id || ""}
                      onChange={(e) =>
                        setMilestoneForm({
                          ...milestoneForm,
                          client_id: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    >
                      <option value="">No client</option>
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                      Project
                    </label>
                    <select
                      value={milestoneForm.project_id || ""}
                      onChange={(e) =>
                        setMilestoneForm({
                          ...milestoneForm,
                          project_id: e.target.value ? parseInt(e.target.value) : null,
                        })
                      }
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm"
                    >
                      <option value="">No project</option>
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={updateMilestone}
                      disabled={!milestoneForm.name.trim()}
                      className="flex-1 px-4 py-2.5 bg-[#264027] hover:bg-[#1a2e1b] disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition-colors"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => {
                        deleteMilestone(editMilestone.id);
                        closeEditMilestone();
                      }}
                      className="px-4 py-2.5 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-red-600 dark:text-red-400 rounded-lg text-sm font-semibold transition-colors"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
